import React, { FunctionComponent, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { AppRoute } from "@app/models/AppRoute";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { openAccessDeniedDialog } from "@app/store/accessDeniedDucks";
import { selectMCInternalExternalPresence } from "@store/Entities/ducks";
import { entitledFundsSelector } from "@store/Entities/selectors";
import { userRolesSelector, userIFCsSelector } from "@myProfile/store/selectors";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";
import AppLoadingIndicator from "@app/components/AppLoadingIndicator";

const USER_ROLE_NAMES = {
  PRIMARY_CONTACT: "Primary Contact",
  SECONDARY_CONTACT: "Secondary Contact",
} as const;

/** Route guard for /manage-contacts. Blocks unauthorized users and shows the Access Denied dialog. */
const ManageContactsRouteGuard: FunctionComponent = ({ children }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { hasInternal, hasExternal } = useAppSelector(selectMCInternalExternalPresence);

  const userRolesState = useAppSelector(userRolesSelector);
  const userRoles = useMemo(() => userRolesState.data || [], [userRolesState.data]);

  const userIFCsState = useAppSelector(userIFCsSelector);
  const entitledFundsState = useAppSelector(entitledFundsSelector);

  const isDialogAlreadyOpen = useAppSelector((state) => state.accessDenied.open);

  const rolesLoaded = userRolesState.data !== null;
  const ifcsLoaded = userIFCsState.data !== null;
  const entitledFundsLoaded = entitledFundsState.data !== null;

  // Wait for ALL 3 data sources — not just IFCs like ProfileRouteGuard.
  // entitledFunds are fetched async after terms rules fire, so they may still
  // be null when the router first renders.
  const permissionsLoaded =
    (ifcsLoaded || !!userIFCsState.error) &&
    (rolesLoaded || !!userRolesState.error) &&
    (entitledFundsLoaded || !!entitledFundsState.error);

  const hasAnyIFCs = useMemo(() => {
    if (!ifcsLoaded) return false;

    return userIFCsState.data !== null && userIFCsState.data.length > 0;
  }, [userIFCsState.data, ifcsLoaded]);

  const isPrimaryContact = useMemo(() => {
    if (!rolesLoaded) return false;

    return userRoles.some((role) => role.roleName === USER_ROLE_NAMES.PRIMARY_CONTACT);
  }, [userRoles, rolesLoaded]);

  const isPrimaryOrSecondary = useMemo(() => {
    if (!rolesLoaded) return false;

    return userRoles.some(
      (role) => role.roleName === USER_ROLE_NAMES.PRIMARY_CONTACT || role.roleName === USER_ROLE_NAMES.SECONDARY_CONTACT
    );
  }, [userRoles, rolesLoaded]);

  const isManageContactsEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.ManageContacts);

  const hasAccess = useMemo(() => {
    if (!isManageContactsEnabled) return false;
    if (!hasAnyIFCs) return false;
    if (hasExternal && isPrimaryOrSecondary) return true;
    if (hasInternal && !hasExternal && isPrimaryContact) return true;

    return false;
  }, [isManageContactsEnabled, hasAnyIFCs, hasExternal, hasInternal, isPrimaryContact, isPrimaryOrSecondary]);

  useEffect(() => {
    if (!permissionsLoaded || hasAccess || isDialogAlreadyOpen) return;

    dispatch(openAccessDeniedDialog("Manage Contacts"));

    // PUSH = navigated from within LPC → go back to the previous page
    // POP / REPLACE = direct URL or external link → redirect to Home
    if (history.action === "PUSH") {
      history.goBack();
    } else {
      history.replace(AppRoute.Home);
    }
  }, [permissionsLoaded, hasAccess, isDialogAlreadyOpen, dispatch, history]);

  if (!permissionsLoaded) {
    return <AppLoadingIndicator />;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return null;
};

export default ManageContactsRouteGuard;
